import { useQuery } from "@tanstack/react-query";

import { fetchJson } from "@/dashboard/hooks/api/fetch-json";
import { isHttpError } from "@/dashboard/lib/api/errors";
import type { Tenant } from "@/dashboard/types/tenant";
import type { User } from "@/dashboard/types/user";

export interface SpoofRealUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface SpoofError {
  attemptedEmail: string;
  reason: "not_found";
}

export interface MeResponse {
  user: User;
  tenants: Tenant[];
  activeTenantId: string;
  isSelfServe: boolean;
  isAccountOverride?: boolean;
  /** From request Host: demo animal subdomain (hawk, parakeet, …). Drives PII blinding with SSR parity. */
  piiDemoBlinding?: boolean;
  realUser?: SpoofRealUser | null;
  isSpoofing?: boolean;
  spoofError?: SpoofError | null;
}

/**
 * Stable query key for the session. Spoof start/stop mutations call
 * `queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })` to refetch.
 * (We used to include the `?spoof=` URL param in the key — that entry path
 * was removed because Vercel served prerendered pages from the edge cache,
 * which never invoked middleware to set the cookie.)
 */
export const ME_QUERY_KEY = ["me"] as const;

export function useMeQuery() {
  return useQuery<MeResponse>({
    queryKey: ME_QUERY_KEY,
    queryFn: () => fetchJson("/api/dashboard/session", { cache: "no-store" }),
    staleTime: 5 * 60_000,
    retry: (failureCount, err: unknown) =>
      isHttpError(err)
        ? err.status !== 401 && failureCount < 3 // do not retry 401
        : failureCount < 3,
  });
}
